import { useEffect, useRef, useState } from 'react'

import { getSmallAssetPath } from '@aytm/lib/common/assets'
import DevLog from '../../../modules/devlog/Module'

import { IQuestionSchema } from '../../types'

import useMountedRef from '@aytm/lib/hooks/useMountedRef'
import { useTimelineFrameInteractionStart } from '../../../modules/timeline/useTimelineFrame'

const EMPTY_IMAGES: string[] = []

export default function useQuestionFramePreloadImages(
  frameInteractionStart: ReturnType<typeof useTimelineFrameInteractionStart>,
  images: string[],
  schema: IQuestionSchema
) {
  const mountedRef = useMountedRef()

  const fullImages = images || EMPTY_IMAGES // EMPTY_IMAGES is used because [] would create a new object on every render
  if (schema.image && !schema.image.expand) fullImages.push(getSmallAssetPath(schema.image))

  const [isLoaded, setIsLoaded] = useState<boolean>(!fullImages?.length)

  const preloadingRef = useRef(false)
  useEffect(() => {
    if (preloadingRef.current) return
    preloadingRef.current = true
    if (!fullImages?.length) {
      if (mountedRef.current) {
        setIsLoaded(true)
        frameInteractionStart()
      }
    } else
      void Promise.all(
        fullImages.map(image =>
          new Promise(resolve => {
            if (!image) resolve('')
            if (image) {
              const img = new Image()
              img.onload = () => resolve({ image })
              img.onerror = () => resolve({ image })
              img.src = image
            }
          }).catch(error => {
            DevLog.error(error, 'SW[useQuestionFramePreloadImages]', 'Failed image: failed to load image', image)
          })
        )
      ).then(() => {
        if (mountedRef.current) {
          setIsLoaded(true)
          frameInteractionStart()
        }
      })
  }, [frameInteractionStart, fullImages, mountedRef])

  return isLoaded
}
